/**
 * `exhaust`, `ready` and `readyAll` (CR 415-416, 315.1). Exhausting an exhausted object (or readying a
 * ready one) does nothing and emits nothing ("no event, no replacement", CR 415.1.b).
 */
import { type Rejection, reject } from '../../kernel/errors';
import { refOf } from '../events/refs';
import type { ObjRef, TableEvent } from '../events/types';
import type { GameState } from '../state/game-state';
import type { PlayerId } from '../state/ids';
import type { GameObject } from '../state/objects';
import { isBoardZone } from '../state/zones';
import { type CommandContext, LIMITS, actorObject, emit } from './context';
import type { TableCommand } from './types';

type Exhaust = Extract<TableCommand, { t: 'exhaust' | 'ready' }>;

/** Board objects that can be exhausted or readied: permanents, runes and legends (not facedown cards or battlefields). */
export function canTurn(object: GameObject): boolean {
  return isBoardZone(object.zone) && object.zone.kind !== 'facedown' && object.category !== 'battlefield';
}

/** Exhausts or readies the named objects; already-turned ones are skipped. */
export function handleExhaust(ctx: CommandContext, cmd: Exhaust): Rejection | undefined {
  const exhausted = cmd.t === 'exhaust';
  if (cmd.objects.length === 0 || cmd.objects.length > LIMITS.objectsPerCommand) {
    return reject('outOfRange', `objects must be 1-${LIMITS.objectsPerCommand}`);
  }
  if (new Set(cmd.objects).size !== cmd.objects.length) return reject('invalidCommand', 'Duplicate objects');
  const objects: GameObject[] = [];
  for (const id of cmd.objects) {
    const object = actorObject(ctx, id);
    if ('rejected' in object) return object;
    if (!canTurn(object)) return reject('invalidCommand', `${id} is not a permanent, rune or legend on the board`);
    objects.push(object);
  }
  const changed: ObjRef[] = [];
  for (const object of objects) {
    if (object.exhausted === exhausted) continue;
    object.exhausted = exhausted;
    changed.push(refOf(ctx.draft, object));
  }
  if (changed.length === 0) return reject('noChange');
  emit(ctx, { type: exhausted ? 'exhausted' : 'readied', actor: ctx.actor, objects: changed });
  return undefined;
}

/**
 * The Awaken Phase's readying for `player` (CR 315.1): every exhausted permanent, rune and legend they
 * control becomes ready. Tier 1 runs it on every turn change (QA 2026-10-08: units moved or played
 * exhausted stayed exhausted until "Ready all" was pressed); tier 2 has its own bookkeeping step with
 * the `autoReady` setting. Emits one `readied` event by `player` when anything changed.
 */
export function awakenReady(draft: GameState, player: PlayerId, events: TableEvent[]): void {
  const changed: ObjRef[] = [];
  for (const object of Object.values(draft.objects)) {
    if (object.controller !== player || !object.exhausted || !canTurn(object)) continue;
    object.exhausted = false;
    changed.push(refOf(draft, object));
  }
  if (changed.length > 0) events.push({ type: 'readied', actor: player, objects: changed });
}

/** Readies every exhausted object the actor controls on the board (Awaken Phase, CR 315.1). */
export function handleReadyAll(ctx: CommandContext): Rejection | undefined {
  const changed: ObjRef[] = [];
  for (const object of Object.values(ctx.draft.objects)) {
    if (object.controller !== ctx.actor || !object.exhausted || !canTurn(object)) continue;
    object.exhausted = false;
    changed.push(refOf(ctx.draft, object));
  }
  if (changed.length === 0) return reject('noChange');
  emit(ctx, { type: 'readied', actor: ctx.actor, objects: changed });
  return undefined;
}
